import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatPrice, formatApiError } from "../lib/api";
import { useCart } from "../contexts/CartContext";

export default function Checkout() {
    const { cart, refresh } = useCart();
    const navigate = useNavigate();
    const [shipping, setShipping] = useState({
        name: "",
        address1: "",
        city: "",
        postal: "",
        country: "United States",
    });
    const [coupon, setCoupon] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subtotal = cart.subtotal || 0;
    let discount = 0;
    if (appliedCoupon) {
        if (appliedCoupon.discount_type === "percent") discount = subtotal * (appliedCoupon.value / 100);
        else discount = appliedCoupon.value;
        discount = Math.min(discount, subtotal);
    }
    const shippingFee = subtotal >= 200 || subtotal === 0 ? 0 : 10;
    const tax = +((subtotal - discount) * 0.08).toFixed(2);
    const total = +(subtotal - discount + shippingFee + tax).toFixed(2);

    const applyCoupon = async () => {
        if (!coupon) return;
        try {
            const { data } = await api.post("/coupons/validate", { code: coupon });
            setAppliedCoupon(data.coupon);
            toast.success("Coupon applied");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Invalid coupon");
        }
    };

    const placeOrder = async () => {
        if (!shipping.name || !shipping.address1 || !shipping.city) {
            return toast.error("Please complete shipping details");
        }
        if (cart.items.length === 0) {
            return toast.error("Your bag is empty");
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/checkout/session", {
                origin_url: window.location.origin,
                coupon_code: appliedCoupon?.code || null,
                shipping,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
            setSubmitting(false);
        }
    };

    if (cart.items.length === 0) {
        return (
            <div className="mx-auto grid min-h-[60vh] max-w-[1480px] place-items-center px-6" data-testid="empty-checkout">
                <div className="text-center">
                    <p className="font-display text-4xl text-white/80">A quiet bag.</p>
                    <p className="mt-3 text-white/50">Add some pieces first.</p>
                    <button
                        onClick={() => navigate("/shop")}
                        className="mt-8 bg-gold px-8 py-3 text-[12px] uppercase tracking-[0.3em] text-obsidian hover:bg-gold-light"
                    >
                        Browse the edit
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-obsidian" data-testid="checkout-page">
            <div className="mx-auto max-w-[1480px] px-4 py-10 sm:px-6 sm:py-12 lg:px-12 lg:py-20">
                <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier checkout</p>
                <h1 className="mt-3 font-display text-4xl text-white sm:text-5xl">Almost yours.</h1>
                <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-16">
                    {/* Form */}
                    <div className="space-y-8 lg:col-span-2">
                        <div className="border border-border-dim p-6 sm:p-8">
                            <h2 className="font-display text-xl text-white sm:text-2xl">Shipping</h2>
                            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                                {[
                                    { k: "name", label: "Full name", span: 2 },
                                    { k: "address1", label: "Street address", span: 2 },
                                    { k: "city", label: "City", span: 1 },
                                    { k: "postal", label: "Postal code", span: 1 },
                                    { k: "country", label: "Country", span: 2 },
                                ].map((f) => (
                                    <div key={f.k} className={f.span === 2 ? "md:col-span-2" : ""}>
                                        <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">
                                            {f.label}
                                        </label>
                                        <input
                                            value={shipping[f.k]}
                                            onChange={(e) => setShipping((s) => ({ ...s, [f.k]: e.target.value }))}
                                            data-testid={`shipping-${f.k}`}
                                            className="mt-2 w-full border border-border-dim bg-transparent px-4 py-3 text-sm text-white outline-none focus:border-gold"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border border-border-dim p-6 sm:p-8">
                            <h2 className="font-display text-xl text-white sm:text-2xl">Coupon</h2>
                            <p className="mt-1 text-xs text-white/40">Try LOVE10 for 10% off your first order</p>
                            <div className="mt-4 flex gap-3">
                                <input
                                    value={coupon}
                                    onChange={(e) => setCoupon(e.target.value)}
                                    placeholder="Coupon code"
                                    data-testid="checkout-coupon-input"
                                    className="flex-1 border border-border-dim bg-transparent px-4 py-3 text-sm text-white outline-none focus:border-gold"
                                />
                                <button
                                    onClick={applyCoupon}
                                    data-testid="checkout-coupon-apply"
                                    className="border border-gold px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-obsidian"
                                >
                                    Apply
                                </button>
                            </div>
                            {appliedCoupon && (
                                <p className="mt-3 text-sm text-gold">
                                    Applied: {appliedCoupon.code} —{" "}
                                    {appliedCoupon.discount_type === "percent"
                                        ? `${appliedCoupon.value}% off`
                                        : `${formatPrice(appliedCoupon.value)} off`}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="space-y-6">
                        <div className="border border-border-dim p-6 sm:p-8">
                            <h2 className="font-display text-xl text-white sm:text-2xl">Your order</h2>
                            <div className="mt-6 max-h-72 space-y-4 overflow-y-auto">
                                {cart.items.map((it) => (
                                    <div key={`${it.product_id}-${it.size}-${it.color}`} className="flex gap-3">
                                        <img
                                            src={it.product.images?.[0]}
                                            alt=""
                                            className="h-16 w-14 flex-shrink-0 object-cover"
                                        />
                                        <div className="flex-1 text-sm">
                                            <p className="text-white">{it.product.name}</p>
                                            <p className="text-white/40">
                                                {[it.size, it.color].filter(Boolean).join(" / ")} × {it.quantity}
                                            </p>
                                        </div>
                                        <p className="text-sm text-gold">{formatPrice(it.line_total)}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 space-y-2 border-t border-border-dim pt-4 text-sm">
                                <Row label="Subtotal" value={formatPrice(subtotal)} />
                                {discount > 0 && (
                                    <Row label="Discount" value={`- ${formatPrice(discount)}`} accent="text-heart" />
                                )}
                                <Row label="Shipping" value={shippingFee === 0 ? "Free" : formatPrice(shippingFee)} />
                                <Row label="Tax (8%)" value={formatPrice(tax)} />
                                <div className="mt-3 flex items-baseline justify-between border-t border-border-dim pt-3">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-white/60">Total</span>
                                    <span data-testid="checkout-total" className="font-display text-3xl text-gold">
                                        {formatPrice(total)}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={placeOrder}
                                disabled={submitting}
                                data-testid="checkout-pay-btn"
                                className="mt-6 w-full bg-gold py-4 text-[12px] uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-gold-light disabled:opacity-50"
                            >
                                {submitting ? "Redirecting…" : "Pay securely · Stripe"}
                            </button>
                            <p className="mt-3 text-center text-xs text-white/40">
                                Payment processed by Stripe. We never store your card.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, accent = "text-white" }) {
    return (
        <div className="flex items-baseline justify-between">
            <span className="text-white/60">{label}</span>
            <span className={accent}>{value}</span>
        </div>
    );
}
